import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Input,
  Button,
  List,
  Spin,
  Card,
  Switch,
  Flex,
  Checkbox,
  Slider,
} from "antd";
import { EditOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import {
  useFetchComplianceByIdQuery,
  useUpdateComplianceMutation,
} from "./compliance.service";
import { BACKEND_URL, quillFormats, quillModules } from "../../constant";
import ReactQuill from "react-quill";
import "./style.css";

const ComplianceChecklistEditPage = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const navigate = useNavigate();

  const { id } = useParams();

  const [newChecklist, setNewChecklist] = useState("");
  const [isInput, setIsInput] = useState(false);
  const [record, setRecord] = useState(false);
  const [isSection, setIsSection] = useState(false);
  const [isTempSlider, setIsTempSlider] = useState(false);
  const [isImage, setIsImage] = useState(false);
  const [imageFile, setImageFile] = useState();
  const [imagePath, setImagePath] = useState();
  const [tempSlider, setTempSlider] = useState(0);
  const [checkContent, setCheckContent] = useState();
  const [charCount, setCharCount] = useState(0);
  const quillRef = useRef(null);
  const CHARACTER_LIMIT = 1500;
  const {
    data = {
      data: { compliance: { title: {} } },
    },
    isFetching,
    isSuccess,
  } = useFetchComplianceByIdQuery(id, { refetchOnMountOrArgChange: true }); // Adjust based on your data fetching logic
  const {
    data: { compliance },
  } = data;

  const [
    updateChecklist,
    { isLoading: isEditLoading, isSuccess: isEditSuccess },
  ] = useUpdateComplianceMutation();

  useEffect(() => {
    if (isSuccess) {
      setNewChecklist(
        compliance[`title_${language}`] || compliance["title"]
      );
      setIsInput(compliance.isInput);
      setIsTempSlider(compliance.isTempSlider);
      setTempSlider(compliance.tempSlider);
      setRecord(compliance.isDateRecord);
      setIsImage(compliance.isImage);
      setIsSection(compliance.isSection);
      if (compliance.imageUrl)
        setImagePath(`${BACKEND_URL}${compliance.imageUrl}`);
    }
  }, [compliance]);

  useEffect(() => {
    if (isEditSuccess) navigate(`/compliance/create/${compliance.type}`);
  }, [isEditLoading]);

  const handleUpdate = async () => {
    try {
      const formData = new FormData();
      formData.append("isDateRecord", record);
      formData.append("isInput", isInput);
      formData.append("isSection", isSection);
      formData.append("isTempSlider", isTempSlider);
      formData.append("isImage", isImage);
      formData.append("title", newChecklist);
      if (isImage && imageFile) formData.append("image", imageFile);

      await updateChecklist({
        id,
        data: formData,
      });
    } catch (err) {
      console.log("Failed to update checklist");
    }
  };

  const handleChange = (html, delta, source, editor) => {
    const text = editor.getText();
    const textLength = text.trim().length;

    if (textLength <= CHARACTER_LIMIT) {
      setNewChecklist(html);
      setCharCount(textLength);
    } else {
      // If user tries to exceed limit, show warning and revert
      if (quillRef.current) {
        const quill = quillRef.current.getEditor();
        quill.root.innerHTML = newChecklist;
      }
    }
  };

  return (
    <Spin spinning={isFetching}>
      <Card
        title={"COMPLIANCE EDIT"}
        style={{ fontSize: "20px" }}
        bordered={false}
        className="opencheck-create-page"
        extra={
          <Flex justify="center">
            <Button
              type="default"
              onClick={() => navigate(`/compliance/create/${compliance.type}`)}
            >
              {"BACK"}
            </Button>
          </Flex>
        }
      >
        <div className="opencheck-create-page-container">
          {/* <Input.TextArea
            type="text"
            rows={3}
            showCount
            maxLength={1500}
            value={newChecklist}
            onChange={(e) => setNewChecklist(e.target.value)}
            placeholder="Enter checklist title"
            style={{ marginBottom: "16px" }}
          /> */}
          <ReactQuill
            ref={quillRef}
            theme="snow"
            value={newChecklist}
            onChange={handleChange}
            modules={quillModules}
            formats={quillFormats}
          />
          <div style={{ textAlign: "right", fontSize: "14px" }}>
            {charCount} / 1500
          </div>
          <div className="opencheck-create-page-container-switch">
            <h4>{"ADD TEXTBOX"}</h4>
            <Switch
              value={isInput}
              onChange={(e) => {
                if (e) setIsSection(false);
                setIsInput(e);
              }}
            />
          </div>
          <div className="opencheck-create-page-container-switch">
            <h4>{"RECORD TIME & DATE"}</h4>
            <Switch
              value={record}
              onChange={(e) => {
                if (e) setIsSection(false);
                setRecord(e);
              }}
            />
          </div>
          <div className="opencheck-create-page-container-switch">
            <h4>{"ADD TEMPERATURE SLIDER"}</h4>
            <Switch
              value={isTempSlider}
              onChange={(e) => {
                if (e) setIsSection(false);
                setIsTempSlider(e);
                setTempSlider(0);
              }}
            />
          </div>
          <div className="opencheck-create-page-container-switch">
            <h4>{"ADD PHOTO"}</h4>
            <Switch
              value={isImage}
              onChange={(e) => {
                if (e) setIsSection(false);
                setIsImage(e);
              }}
            />
          </div>
          <div className="opencheck-create-page-container-switch">
            <h4>{"SECTION"}</h4>
            <Switch
              value={isSection}
              onChange={(e) => {
                if (e) {
                  setIsInput(false);
                  setRecord(false);
                  setIsTempSlider(false);
                  setTempSlider(0);
                  setIsImage(false);
                }
                setIsSection(e);
              }}
            />
          </div>
          <div className="opencheck-create-page-container-button">
            <Button
              color="green"
              variant="solid"
              icon={<EditOutlined />}
              size="small"
              onClick={handleUpdate}
              loading={isEditLoading}
            >
              {"Update"}
            </Button>
          </div>
        </div>

        <List
          className="opencheck-create-page-insight"
          itemLayout="horizontal"
          dataSource={[
            {
              isSection: isSection,
              title: isSection ? "" : newChecklist,
              description: isSection ? (
                newChecklist
              ) : (
                <div className="hothold-check-add-task">
                  {isInput && (
                    <div style={{ padding: "5px 15px" }}>
                      <Input
                        style={{ background: "transparent" }}
                        value={checkContent}
                        onChange={(e) => setCheckContent(e.target.value)}
                      />
                    </div>
                  )}
                  {isTempSlider && (
                    <>
                      <div
                        style={{
                          fontSize: "1.5rem",
                          textAlign: "center",
                          marginTop: 10,
                          fontWeight: "bold",
                        }}
                      >
                        {tempSlider}
                        °C
                      </div>
                      <Slider
                        styles={{
                          track: {
                            background: "transparent",
                          },
                          tracks: {
                            background: `rgb(109, 179, 63)`,
                          },
                        }}
                        min={-30}
                        max={120}
                        step={0.1}
                        onChange={(temp) => setTempSlider(temp)}
                        defaultValue={tempSlider}
                      />
                    </>
                  )}
                  {record && <div>*RECORD TIME AND DATE AUTOMATICALLY*</div>}
                  <label style={{ cursor: "pointer" }}>
                    {isImage &&
                      (imagePath ? (
                        <img
                          crossOrigin="anonymous"
                          src={imagePath}
                          alt="Uploaded compliance image"
                          style={{
                            objectFit: "cover",
                            border: "1px solid #d9d9d9",
                            borderRadius: "6px",
                            width: "50%",
                            height: "auto",
                          }}
                        />
                      ) : (
                        <div className="upload-photo-button">Upload Photo</div>
                      ))}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        if (e.target.files && e.target.files[0]) {
                          if (e.target.files[0].type.startsWith("image/")) {
                            setImageFile(e.target.files[0]);
                            setImagePath(
                              URL.createObjectURL(e.target.files[0])
                            );
                          }
                        }
                      }}
                      hidden
                    />
                  </label>
                </div>
              ),
            },
          ]}
          renderItem={(checklist, index) => (
            <List.Item
              className={
                checklist.isSection ? "background-section" : "background-task"
              }
            >
              <List.Item.Meta
                title={
                  checklist.title !== "" && (
                    <div
                      className="compliance-checklist-content"
                      dangerouslySetInnerHTML={{ __html: checklist.title }}
                    ></div>
                  )
                }
                description={
                  isSection ? (
                    <div
                      className="compliance-checklist-content"
                      dangerouslySetInnerHTML={{
                        __html: checklist.description,
                      }}
                    ></div>
                  ) : (
                    checklist.description
                  )
                }
              />
            </List.Item>
          )}
        />
      </Card>
    </Spin>
  );
};

export default ComplianceChecklistEditPage;
